"use client";

import type { ClerkAPIResponseError } from "@clerk/shared/error";
import type { OAuthStrategy } from "@clerk/shared/types";
import { useSignIn, useSignUp } from "@klarify/auth/client";
import { getLocalizedPathname } from "@klarify/i18n/path";
import {
	isCaptchaError,
	isDeadTicketError,
} from "@klarify/ui/components/auth/clerk-errors";
import { EmailVerificationForm } from "@klarify/ui/components/auth/email-verification-form";
import { Button } from "@klarify/ui/components/ui/button";
import { Input } from "@klarify/ui/components/ui/input";
import { Label } from "@klarify/ui/components/ui/label";
import { Trans, useLingui } from "@lingui/react/macro";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import GoogleGIcon from "../svg/google-g-icon";

interface CustomSignUpProps {
	signInUrl?: string;
	forceRedirectUrl?: string;
	redirectUrl?: string;
	initialEmail?: string;
	/** When set, uses the Clerk ticket strategy (org invitation flow) instead of the normal email+password flow. */
	ticket?: string;
	/** Called once a dead ticket has been dropped and this is a normal sign-up. */
	onTicketRejected?: () => void;
	onVerificationStateChange?: (isPendingVerification: boolean) => void;
	/** Resolves once the role/time cookies are written; awaited before signUp.create. */
	awaitCookies: () => Promise<void>;
	/** Re-runs the cookie writes; awaited before OAuth so an expired redirect-time cookie is rewritten. */
	refreshCookies: () => Promise<void>;
}

export function CustomSignUp({
	signInUrl,
	forceRedirectUrl,
	redirectUrl,
	initialEmail,
	ticket,
	onTicketRejected,
	onVerificationStateChange,
	awaitCookies,
	refreshCookies,
}: CustomSignUpProps) {
	const { t } = useLingui();
	const { isLoaded, signUp, setActive } = useSignUp();
	const { isLoaded: isSignInLoaded, signIn } = useSignIn();
	// Dropped once Clerk refuses the ticket, so the plain email+password form
	// below becomes reachable instead of every retry failing the same way.
	const [activeTicket, setActiveTicket] = useState(ticket);
	const ticketRejected = Boolean(ticket) && !activeTicket;
	const [emailAddress, setEmailAddress] = useState(initialEmail ?? "");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [marketingOptIn, setMarketingOptIn] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [pendingVerification, setPendingVerification] = useState(false);
	const [firstNameError, setFirstNameError] = useState("");
	const [lastNameError, setLastNameError] = useState("");
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [termsError, setTermsError] = useState("");
	const [generalError, setGeneralError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		onVerificationStateChange?.(pendingVerification);
	}, [pendingVerification, onVerificationStateChange]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!signUp) {
			return;
		}

		if (!acceptedTerms) {
			setTermsError(
				t`You must accept the Terms of Service and Privacy Policy to continue.`,
			);
			return;
		}

		setIsSubmitting(true);
		setFirstNameError("");
		setLastNameError("");
		setEmailError("");
		setPasswordError("");
		setTermsError("");
		setGeneralError("");

		try {
			// Cookies drive /auth/redirect role + is-new-user routing; must land first.
			await awaitCookies();
			if (activeTicket) {
				// Invitation flow: ticket strategy auto-verifies the email
				const result = await signUp
					.create({
						strategy: "ticket",
						ticket: activeTicket,
						firstName,
						lastName,
						password,
						legalAccepted: acceptedTerms,
						unsafeMetadata: {
							marketingOptIn,
						},
					})
					.catch((err: unknown) => {
						if (!isDeadTicketError(err)) {
							throw err;
						}
						return null;
					});

				// Clerk's 30-day ticket expiry outlives the clinic's open invitation.
				// Degrade to a normal sign-up — the server matches the verified address back.
				if (!result) {
					setActiveTicket(undefined);
					onTicketRejected?.();
					setIsSubmitting(false);
					return;
				}

				if (result.status === "complete") {
					await setActive({ session: result.createdSessionId });
					window.location.href =
						forceRedirectUrl ??
						redirectUrl ??
						getLocalizedPathname("/auth/redirect");
					return;
				}

				setGeneralError(t`Sign-up could not be completed. Please try again.`);
				setIsSubmitting(false);
				return;
			}

			await signUp.create({
				emailAddress,
				password,
				firstName,
				lastName,
				legalAccepted: acceptedTerms,
				unsafeMetadata: {
					marketingOptIn,
				},
			});

			// Send the email verification code
			await signUp.prepareEmailAddressVerification({ strategy: "email_code" });

			// Display the verification form
			setPendingVerification(true);
		} catch (err: unknown) {
			if (isCaptchaError(err)) {
				// The CAPTCHA token was invalid or missing. A full reload forces
				// Clerk to reinitialize the widget with a fresh token. Keep
				// isSubmitting=true so the button stays disabled during reload.
				console.warn(
					"CAPTCHA error detected – reloading page to reinitialize bot protection",
				);
				setGeneralError(t`Verifying your browser, please wait…`);
				window.location.reload();
				return;
			}

			const clerkError = err as ClerkAPIResponseError;
			const errorMessage =
				clerkError.longMessage ??
				clerkError?.errors?.[0]?.longMessage ??
				t`An error occurred during sign up.`;
			const errorParam = clerkError.errors?.[0]?.meta?.paramName;

			// The email field is hidden during the ticket flow, so a rejection blaming
			// it (account created for the invited address between invite and accept)
			// must fall back to the general error box or it renders nowhere.
			if (errorParam === "email_address" && !activeTicket) {
				setEmailError(errorMessage);
			} else if (errorParam === "password") {
				setPasswordError(errorMessage);
			} else if (errorParam === "first_name") {
				setFirstNameError(errorMessage);
			} else if (errorParam === "last_name") {
				setLastNameError(errorMessage);
			} else {
				setGeneralError(errorMessage);
			}
			setIsSubmitting(false);
		}
	};

	const signUpWith = async (strategy: OAuthStrategy) => {
		if (!signIn) {
			return;
		}

		setIsSubmitting(true);
		setFirstNameError("");
		setLastNameError("");
		setEmailError("");
		setPasswordError("");
		setTermsError("");
		setGeneralError("");

		try {
			// Cookies drive /auth/redirect role + is-new-user routing; they must land
			// before the OAuth navigation leaves the page. A fresh run also rewrites
			// the redirect-time cookie if its 1h maxAge lapsed while the tab idled.
			await refreshCookies();

			return signIn
				.authenticateWithRedirect({
					strategy,
					redirectUrl: getLocalizedPathname("/auth/sso-callback"),
					redirectUrlComplete:
						forceRedirectUrl ??
						redirectUrl ??
						getLocalizedPathname("/auth/redirect"),
					continueSignUp: true,
				})
				.catch((err) => {
					console.log(err);
					console.error(err, null, 2);
				});
		} catch (err: unknown) {
			const clerkError = err as ClerkAPIResponseError;
			setGeneralError(
				clerkError.longMessage ??
					clerkError?.errors?.[0]?.longMessage ??
					t`An error occurred during sign up.`,
			);
			setIsSubmitting(false);
		}
	};
	// Paint the form from SSR; only the create/OAuth actions wait for Clerk.
	const actionsReady = isLoaded && isSignInLoaded;

	return (
		<>
			{/* clerk-captcha must always stay mounted before signUp.create() runs.
			Turnstile's flexible widget is 65px tall; floor the div only once Clerk
			injects the widget so a slow challenge iframe cannot shift the card. The
			empty happy path (invisible challenge) must keep zero height, and its
			negative margin cancels the auth column gap. */}
			<div
				id="clerk-captcha"
				className="not-empty:min-h-[65px] empty:-mb-4"
				data-cl-theme="light"
				data-cl-size="flexible"
			/>

			<div className="w-full max-w-md rounded-xl border bg-white p-7 shadow-custom lg:p-8">
				<div className="mb-5">
					<h1 className="font-bold text-2xl text-klarify-neutral-950 tracking-tight">
						{pendingVerification ? (
							<Trans>Verify your email</Trans>
						) : (
							<Trans>Create your account</Trans>
						)}
					</h1>
					{!pendingVerification && (
						<p className="mt-1 text-[13px] text-muted-foreground">
							{activeTicket ? (
								<Trans>to accept your invitation and join the clinic</Trans>
							) : (
								<Trans>to continue to Klarify</Trans>
							)}
						</p>
					)}
				</div>

				{!pendingVerification ? (
					<>
						{ticketRejected && (
							<div className="mb-4 rounded-md border border-klarify-gray-mod-200 bg-klarify-gray-mod-50 p-3 text-klarify-gray-mod-700 text-sm">
								<Trans>
									This invitation link has expired. Create your account with the
									email address it was sent to and we'll add you to the clinic.
								</Trans>
							</div>
						)}

						{!activeTicket && (
							<>
								<Button
									type="button"
									onClick={() => signUpWith("oauth_google")}
									disabled={isSubmitting || !actionsReady}
									aria-busy={!actionsReady}
									variant="outline"
									className="w-full gap-2"
								>
									<GoogleGIcon
										width={16}
										height={16}
									/>
									<Trans>Continue with Google</Trans>
								</Button>

								<div className="relative my-4">
									<div className="absolute inset-0 flex items-center">
										<div className="w-full border-klarify-gray-mod-200 border-t" />
									</div>
									<div className="relative flex justify-center text-sm">
										<span className="bg-white px-2 text-klarify-gray-mod-400">
											<Trans>or</Trans>
										</span>
									</div>
								</div>
							</>
						)}

						<form
							onSubmit={handleSubmit}
							className="space-y-4"
						>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<Label
										htmlFor="firstName"
										className="mb-1.5 block font-medium text-klarify-gray-mod-700"
									>
										<Trans>First name</Trans>
									</Label>
									<Input
										type="text"
										id="firstName"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										inputClassName={
											firstNameError
												? "border-destructive focus-visible:ring-destructive"
												: ""
										}
										required
									/>
									{firstNameError && (
										<p className="mt-1.5 text-destructive text-sm">
											{firstNameError}
										</p>
									)}
								</div>
								<div>
									<Label
										htmlFor="lastName"
										className="mb-1.5 block font-medium text-klarify-gray-mod-700"
									>
										<Trans>Last name</Trans>
									</Label>
									<Input
										type="text"
										id="lastName"
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										inputClassName={
											lastNameError
												? "border-destructive focus-visible:ring-destructive"
												: ""
										}
										required
									/>
									{lastNameError && (
										<p className="mt-1.5 text-destructive text-sm">
											{lastNameError}
										</p>
									)}
								</div>
							</div>

							{!activeTicket && (
								<div>
									<Label
										htmlFor="email"
										className="mb-1.5 block font-medium text-klarify-gray-mod-700"
									>
										<Trans>Email address</Trans>
									</Label>
									<Input
										type="email"
										id="email"
										value={emailAddress}
										onChange={(e) => setEmailAddress(e.target.value)}
										inputClassName={
											emailError
												? "border-destructive focus-visible:ring-destructive"
												: ""
										}
										required
									/>
									{emailError && (
										<p className="mt-1.5 text-destructive text-sm">
											{emailError}
										</p>
									)}
								</div>
							)}

							<div>
								<Label
									htmlFor="password"
									className="mb-1.5 block font-medium text-klarify-gray-mod-700"
								>
									<Trans>Password</Trans>
								</Label>
								<div className="relative">
									<Input
										type={showPassword ? "text" : "password"}
										id="password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										inputClassName={
											passwordError
												? "border-destructive pr-10 focus-visible:ring-destructive"
												: "pr-10"
										}
										required
										minLength={8}
									/>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2 cursor-pointer p-0 text-klarify-gray-mod-500 hover:bg-transparent hover:text-klarify-gray-mod-700"
										aria-label={
											showPassword ? t`Hide password` : t`Show password`
										}
									>
										{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
									</Button>
								</div>
								{passwordError && (
									<p className="mt-1.5 text-destructive text-sm">
										{passwordError}
									</p>
								)}
							</div>

							<div className="space-y-3">
								<div className="rounded-md border border-klarify-gray-mod-200 bg-klarify-gray-mod-50 p-3">
									<label className="flex cursor-pointer items-start gap-3">
										<input
											type="checkbox"
											checked={acceptedTerms}
											onChange={(e) => setAcceptedTerms(e.target.checked)}
											className="mt-0.5 min-h-4 min-w-4 cursor-pointer rounded border-klarify-gray-mod-300 text-primary focus:ring-primary"
											required
										/>
										<span className="text-klarify-gray-mod-700 text-sm">
											<Trans>I accept the</Trans>{" "}
											<a
												href="https://www.klarify.ca/terms-of-service"
												target="_blank"
												rel="noopener noreferrer"
												className="font-medium text-primary underline hover:text-primary/90"
											>
												<Trans>Terms of Service</Trans>
											</a>{" "}
											<Trans>and</Trans>{" "}
											<a
												href="https://www.klarify.ca/privacy-policy"
												target="_blank"
												rel="noopener noreferrer"
												className="font-medium text-primary underline hover:text-primary/90"
											>
												<Trans>Privacy Policy</Trans>
											</a>
										</span>
									</label>
									{termsError && (
										<p className="mt-2 text-destructive text-sm">
											{termsError}
										</p>
									)}
								</div>

								<div className="rounded-md border border-klarify-gray-mod-200 bg-klarify-gray-mod-50 p-3">
									<label className="flex cursor-pointer items-start gap-3">
										<input
											type="checkbox"
											checked={marketingOptIn}
											onChange={(e) => setMarketingOptIn(e.target.checked)}
											className="mt-0.5 min-h-4 min-w-4 cursor-pointer rounded border-klarify-gray-mod-300 text-primary focus:ring-primary"
										/>
										<span className="text-klarify-gray-mod-700 text-sm">
											<Trans>
												I want to receive product updates and tips from Klarify
											</Trans>
										</span>
									</label>
								</div>
							</div>

							{generalError && (
								<div className="rounded-md bg-destructive/5 p-3 text-destructive text-sm">
									{generalError}
								</div>
							)}

							{activeTicket && generalError && signInUrl && (
								<div className="text-center text-klarify-gray-mod-600 text-sm">
									<Trans>Already have an account?</Trans>{" "}
									<a
										href={signInUrl}
										className="font-medium text-primary hover:text-primary/90"
									>
										<Trans>Sign in</Trans>
									</a>
								</div>
							)}

							<Button
								type="submit"
								disabled={isSubmitting || !actionsReady}
								aria-busy={!actionsReady}
								className="mt-2 h-11 w-full font-semibold"
							>
								{isSubmitting ? (
									<Trans>Creating account...</Trans>
								) : activeTicket ? (
									<Trans>Create account & join clinic</Trans>
								) : (
									<Trans>Continue</Trans>
								)}
							</Button>

							{!activeTicket && signInUrl && (
								<div className="mt-2 text-center text-klarify-gray-mod-600 text-sm">
									<Trans>Already have an account?</Trans>{" "}
									<a
										href={signInUrl}
										className="font-medium text-primary hover:text-primary/90"
									>
										<Trans>Sign in</Trans>
									</a>
								</div>
							)}
						</form>
					</>
				) : (
					<EmailVerificationForm
						forceRedirectUrl={forceRedirectUrl}
						redirectUrl={redirectUrl}
					/>
				)}
			</div>
		</>
	);
}
